"use client";

import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import CustomProcedure from "./CustomProcedure";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getModuleByName } from "@/constants/procedure-modules";
import { loadSubstantiveProcedures } from "@/utils/substantive-procedures-utils";
import AgeingAnalysisModule from "../modules/AgeingAnalysisModule";
import CompositionAnalysisModule from "../modules/CompositionAnalysisModule";

interface CustomProcedureRouteProps {
  route: string;
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

export default function CustomProcedureRoute({
  route,
  onBack,
  setActiveSection,
}: CustomProcedureRouteProps) {
  const { isArchivalMode } = useArchivalMode();
  const [loading, setLoading] = useState(true);
  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [procedureName, setProcedureName] = useState<string>("");
  const [workspacePrefix, setWorkspacePrefix] = useState<string>("");
  const [area, setArea] = useState<"Planning" | "Execution" | "ConcludingProcedures">("Execution");
  const [error, setError] = useState<string>("");
  const [backRoute, setBackRoute] = useState<string>("");
  const [isModule, setIsModule] = useState<boolean>(false);
  const [moduleComponent, setModuleComponent] = useState<string>("");

  useEffect(() => {
    const loadProcedureInfo = async () => {
      try {
        setLoading(true);
        setError("");

        // Route format: custom-procedure:<workspace>:<procedure-name>
        // Both workspace and procedure name are URL encoded
        if (route.startsWith("custom-procedure:")) {
          const parts = route.split(":");
          if (parts.length >= 3) {
            const encodedWorkspace = parts[1];
            const encodedProcedure = parts.slice(2).join(":"); // In case procedure name has colons
            const decodedWorkspace = decodeURIComponent(encodedWorkspace);
            const decodedProcedure = decodeURIComponent(encodedProcedure);
            
            setWorkspaceName(decodedWorkspace);
            setProcedureName(decodedProcedure);
            
            // Check if this is a module procedure
            const module = getModuleByName(decodedProcedure);
            if (module) {
              setIsModule(true);
              setModuleComponent(module.componentName);
            } else {
              setIsModule(false);
              setModuleComponent("");
            }
            
            // Load Sections.json to get workspace prefix and area
            if ((window as any).sharePointAPI?.downloadSectionsJson) {
              const result = await (window as any).sharePointAPI.downloadSectionsJson();
              if (result?.success && result?.data) {
                // Search through all areas to find matching workspace
                let found = false;
                for (const [areaKey, workspaces] of Object.entries(result.data)) {
                  if (areaKey === "Planning" || areaKey === "Execution" || areaKey === "ConcludingProcedures") {
                    const workspaceMap = workspaces as Record<string, string>;
                    if (workspaceMap[decodedWorkspace]) {
                      const prefix = workspaceMap[decodedWorkspace];
                      setWorkspacePrefix(prefix);
                      setArea(areaKey as "Planning" | "Execution" | "ConcludingProcedures");
                      
                      // Determine back route
                      // For custom workspaces, use dynamic-workspace-substantive route
                      // For standard workspaces, use the prefix-based route
                      const isCustomWorkspace = !["Employee Benefits Expense", "Property, Plant & Equipment", "Intangible Assets", "Lease Liability", "Lease Liability - Ind AS", "Right-of-Use Assets", "Cost of Materials Consumed", "Trade Payables", "Inventory", "Other Assets", "Other Financial Assets", "Trade Receivables", "Depreciation", "Impairment Testing", "Other Expenses", "Borrowings", "Cash & Cash Equivalents", "Finance Cost", "Income Tax", "Investments", "Other Income", "Other Liabilities", "Other Financial Liabilities", "Other Receivables", "Provisions & Contingent Liabilities", "Going Concern", "Litigations & Claims", "Opening Balance Testing", "Related Party Disclosure", "Equity", "Management Use of Expert", "Use of Auditor's Specialist", "Compliance with Laws and Regulations", "Review of Estimates for Management Bias", "Internal Audit", "Financial Closing & Reporting Process", "Management Override of Controls"].includes(decodedWorkspace);
                      
                      if (isCustomWorkspace) {
                        // Custom workspace - use dynamic route
                        setBackRoute(`dynamic-workspace-substantive:${areaKey}:${prefix}:${encodeURIComponent(decodedWorkspace)}`);
                      } else if (decodedWorkspace === "Employee Benefits Expense") {
                        setBackRoute("payroll-substantive");
                      } else {
                        // For other standard workspaces, construct route based on prefix
                        // This matches the pattern used in left-panel.tsx
                        const prefixLower = prefix.toLowerCase();
                        setBackRoute(`${prefixLower}-substantive`);
                      }
                      
                      found = true;
                      break;
                    }
                  }
                }

                if (!found) {
                  setError("Workspace not found in Sections.json");
                }
              } else {
                setError("Failed to load workspace information");
              }
            } else {
              setError("SharePoint API not available");
            }
          } else {
            setError("Invalid route format");
          }
        } else {
          setError("Invalid route format");
        }
      } catch (err) {
        console.error("Failed to load custom procedure info", err);
        setError("Failed to load procedure information");
      } finally {
        setLoading(false);
      }
    };

    loadProcedureInfo();
  }, [route]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  if (error || !workspaceName || !procedureName || !workspacePrefix) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white">
        <div className="text-red-400 mb-2">{error || "Failed to load procedure"}</div>
        {onBack && (
          <button
            onClick={onBack}
            disabled={isArchivalMode}
            className="mt-4 px-4 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Go Back
          </button>
        )}
      </div>
    );
  }

  const handleBack = () => {
    if (backRoute && setActiveSection) {
      setActiveSection(backRoute);
    } else if (onBack) {
      onBack();
    }
  };

  // Render module component if this is a module
  if (isModule && moduleComponent) {
    if (moduleComponent === "AgeingAnalysisModule") {
      return (
        <AgeingAnalysisModule
          onBack={handleBack}
          area={area}
          section={workspaceName}
          workspaceName={workspaceName}
          workspacePrefix={workspacePrefix}
          procedureName={procedureName}
        />
      );
    }
    if (moduleComponent === "CompositionAnalysisModule") {
      return (
        <CompositionAnalysisModule
          onBack={handleBack}
          area={area}
          section={workspaceName}
          workspaceName={workspaceName}
          workspacePrefix={workspacePrefix}
          procedureName={procedureName}
        />
      );
    }
    // Add other module components here as they are created
  }

  // Render custom procedure for non-module procedures
  return (
    <CustomProcedure
      workspaceName={workspaceName}
      procedureName={procedureName}
      workspacePrefix={workspacePrefix}
      area={area}
      onBack={handleBack}
    />
  );
}

